import useAuthRedirect from '@/hooks/useAuthRedirect';
import usePermission from '@/hooks/usePermission';
import { message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ROUTE } from '@/config';
import { useUserStore } from '@/stores';
import { getDefaultRoute } from '@/utils';

interface PrivateRouteProps {
  element: JSX.Element;
  requiredPermissions?: string[];
}

const PrivateRoute = ({ element, requiredPermissions }: PrivateRouteProps) => {
  useAuthRedirect();

  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);

  // Wait for user data to load before checking permissions
  // Check if permissions are actually defined (not undefined)
  // undefined = still loading, [] or array = loaded
  const permissionsLoaded = user?.permissions !== undefined;

  // Calculate permissions (must be called on every render - Rules of Hooks)
  const hasRequiredPermissions =
    !requiredPermissions ||
    requiredPermissions.length === 0 ||
    hasPermission(...requiredPermissions);

  // Effect to show error and redirect (must be called on every render - Rules of Hooks)
  useEffect(() => {
    // Only run this effect after permissions are loaded
    if (permissionsLoaded && !hasRequiredPermissions) {
      message.error(t('you_do_not_have_permission_to_view_this_page'));
      const defaultRoute = getDefaultRoute(hasPermission);
      navigate(defaultRoute, { replace: true });
    }
  }, [permissionsLoaded, hasRequiredPermissions, navigate, t, hasPermission]);

  // NOW we can do conditional returns (after all hooks are called)
  // Show loading spinner while waiting for user data
  if (!user || !permissionsLoaded) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Spin size='large' />
      </div>
    );
  }

  // If no permissions, return null (redirect happens in useEffect above)
  if (!hasRequiredPermissions) {
    return null;
  }

  return element;
};

export default PrivateRoute;
